import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  IconButton,
  Input,
  Kbd,
  Select,
  Separator,
  SimpleTooltip,
} from '@aibi/ui';
import {
  Bot,
  ChartNoAxesCombined,
  Database,
  Download,
  Globe,
  Moon,
  PanelLeft,
  PanelRight,
  Plus,
  Redo2,
  Settings,
  Sparkles,
  Sun,
  Undo2,
} from 'lucide-react';
import { useState, useSyncExternalStore } from 'react';
import { useServices } from '../../../app/services';
import { PublishReportDialog } from '../../../report/components/PublishReportDialog';
import {
  hasPublishableChanges,
  readPublicationSnapshot,
  subscribePublicationChanges,
} from '../../../report/publish-snapshot';
import type { SemanticModelSummary } from '../../services/semantic-model-catalog';
import type { SaveStatus } from '../../state/use-autosave';
import { useUiDispatch, useUiState } from '../../state/ui-state';
import { useWorkspace, useWorkspaceActions } from '../../state/workspace-context';

const SAVE_LABEL: Readonly<Record<SaveStatus, string>> = {
  saved: 'Borrador guardado',
  pending: 'Guardando...',
  error: 'No se pudo guardar',
};

export interface TopBarProps {
  readonly models: readonly SemanticModelSummary[];
  readonly onModelChange: (modelId: string) => void;
  readonly saveStatus: SaveStatus;
}

/** Barra superior: modelo, nombre del analisis, historial, acciones globales y paneles. */
export function TopBar({ models, onModelChange, saveStatus }: TopBarProps) {
  const { document, canUndo, canRedo } = useWorkspace();
  const { execute, undo, redo } = useWorkspaceActions();
  const ui = useUiState();
  const uiDispatch = useUiDispatch();
  const { admin, reports, queryResultSource } = useServices();
  const [publishOpen, setPublishOpen] = useState(false);
  const canPublish = admin !== undefined && reports !== undefined;
  const publicationRaw = useSyncExternalStore(
    (onChange) => subscribePublicationChanges(document.id, onChange),
    () => window.localStorage.getItem(`aibi.report-pub.${document.id}`),
    () => null,
  );
  const publishSnapshot =
    publicationRaw === null ? null : readPublicationSnapshot(window.localStorage, document.id);
  const publishable = hasPublishableChanges(document, publishSnapshot);
  // Sin cambios: el boton solo sirve para gestionar un reporte ya publicado (enlace / retirar).
  // Publicar o actualizar dentro del dialogo sigue deshabilitado si no hay cambios.
  const publishDisabled = !canPublish || (!publishable && publishSnapshot === null);
  const publishTooltip = !canPublish
    ? 'Publicar reportes requiere el servidor'
    : document.cards.length === 0 && publishSnapshot === null
      ? 'Agregue al menos una visualizacion para publicar'
      : publishable
        ? 'Generar un enlace para ver este analisis como reporte'
        : 'Sin cambios desde la ultima publicacion';

  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border bg-surface px-3">
      <div className="flex items-center gap-2 pr-1">
        <span className="flex size-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
          <ChartNoAxesCombined aria-hidden className="size-4" />
        </span>
        <h1 className="text-sm font-semibold">AI BI Platform</h1>
      </div>
      <Separator orientation="vertical" className="h-6" />
      <Select
        aria-label="Modelo semantico"
        value={document.semanticModelId}
        className="h-7 w-44 text-xs"
        onChange={(event) => {
          onModelChange(event.target.value);
        }}
      >
        {models.map((model) => (
          <option key={model.id} value={model.id}>
            {model.name}
          </option>
        ))}
      </Select>
      <Input
        key={document.name}
        aria-label="Nombre del analisis"
        defaultValue={document.name}
        className="h-7 w-56 border-transparent bg-transparent text-sm font-medium hover:border-input"
        onBlur={(event) => {
          const name = event.target.value;
          if (name.trim() === '') event.target.value = document.name;
          else if (name !== document.name) execute({ type: 'rename-workspace', name });
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
          if (event.key === 'Escape') {
            event.currentTarget.value = document.name;
            event.currentTarget.blur();
          }
        }}
      />
      <span
        aria-live="polite"
        className={
          saveStatus === 'error' ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'
        }
      >
        {SAVE_LABEL[saveStatus]}
      </span>

      <div className="ml-auto flex items-center gap-1">
        <SimpleTooltip
          content={
            <span className="inline-flex items-center gap-1.5">
              Deshacer <Kbd>Ctrl</Kbd>
              <Kbd>Z</Kbd>
            </span>
          }
        >
          <IconButton label="Deshacer" size="default" disabled={!canUndo} onClick={undo}>
            <Undo2 />
          </IconButton>
        </SimpleTooltip>
        <SimpleTooltip
          content={
            <span className="inline-flex items-center gap-1.5">
              Rehacer <Kbd>Ctrl</Kbd>
              <Kbd>Y</Kbd>
            </span>
          }
        >
          <IconButton label="Rehacer" size="default" disabled={!canRedo} onClick={redo}>
            <Redo2 />
          </IconButton>
        </SimpleTooltip>
        <Separator orientation="vertical" className="mx-1 h-6" />
        <SimpleTooltip
          content={
            <span className="inline-flex items-center gap-1.5">
              Nueva visualizacion <Kbd>Ctrl</Kbd>
              <Kbd>Shift</Kbd>
              <Kbd>N</Kbd>
            </span>
          }
        >
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              execute({ type: 'add-card' });
            }}
          >
            <Plus /> Nueva visualizacion
          </Button>
        </SimpleTooltip>
        <SimpleTooltip content={publishTooltip}>
          {publishDisabled ? (
            <span tabIndex={0} className="inline-flex rounded-md">
              <Button size="sm" variant="outline" disabled>
                <Globe /> Publicar
              </Button>
            </span>
          ) : (
            <Button
              size="sm"
              variant="outline"
              aria-haspopup="dialog"
              onClick={() => {
                setPublishOpen(true);
              }}
            >
              <Globe /> Publicar
            </Button>
          )}
        </SimpleTooltip>
        {canPublish && (
          <PublishReportDialog
            open={publishOpen}
            onOpenChange={setPublishOpen}
            document={document}
            dataMode={queryResultSource.modeFor?.(document.semanticModelId) ?? null}
          />
        )}
        <SimpleTooltip content="Exportar estara disponible en una proxima version">
          <span tabIndex={0} className="inline-flex rounded-md">
            <IconButton label="Exportar (no disponible)" size="default" disabled>
              <Download />
            </IconButton>
          </span>
        </SimpleTooltip>
        <Separator orientation="vertical" className="mx-1 h-6" />
        <SimpleTooltip
          content={
            <span className="inline-flex items-center gap-1.5">
              {ui.explorerOpen ? 'Ocultar explorador' : 'Mostrar explorador'} <Kbd>Ctrl</Kbd>
              <Kbd>B</Kbd>
            </span>
          }
        >
          <IconButton
            label="Explorador de campos"
            size="default"
            active={ui.explorerOpen}
            onClick={() => {
              uiDispatch({ type: 'toggle-explorer' });
            }}
          >
            <PanelLeft />
          </IconButton>
        </SimpleTooltip>
        <SimpleTooltip
          content={
            <span className="inline-flex items-center gap-1.5">
              {ui.propertiesOpen ? 'Ocultar propiedades' : 'Mostrar propiedades'} <Kbd>Ctrl</Kbd>
              <Kbd>\</Kbd>
            </span>
          }
        >
          <IconButton
            label="Panel de propiedades"
            size="default"
            active={ui.propertiesOpen}
            onClick={() => {
              uiDispatch({ type: 'toggle-properties' });
            }}
          >
            <PanelRight />
          </IconButton>
        </SimpleTooltip>
        <DropdownMenu>
          <SimpleTooltip content="Configuracion">
            <DropdownMenuTrigger asChild>
              <IconButton label="Configuracion" size="default">
                <Settings />
              </IconButton>
            </DropdownMenuTrigger>
          </SimpleTooltip>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Configuracion</DropdownMenuLabel>
            <DropdownMenuItem
              onSelect={() => {
                uiDispatch({ type: 'set-active-view', view: 'connections' });
              }}
            >
              <Database /> Conexiones de datos
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                uiDispatch({ type: 'set-active-view', view: 'assistant' });
              }}
            >
              <Bot /> Asistente de IA
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <SimpleTooltip content={ui.colorScheme === 'dark' ? 'Tema claro' : 'Tema oscuro'}>
          <IconButton
            label={ui.colorScheme === 'dark' ? 'Activar tema claro' : 'Activar tema oscuro'}
            size="default"
            onClick={() => {
              uiDispatch({ type: 'toggle-color-scheme' });
            }}
          >
            {ui.colorScheme === 'dark' ? <Sun /> : <Moon />}
          </IconButton>
        </SimpleTooltip>
        <SimpleTooltip
          content={
            <span className="inline-flex items-center gap-1.5">
              Preguntar a la IA <Kbd>Ctrl</Kbd>
              <Kbd>J</Kbd>
            </span>
          }
        >
          <Button
            size="sm"
            className="ml-1"
            aria-expanded={ui.aiPanelOpen}
            onClick={() => {
              uiDispatch({ type: 'set-ai-panel', open: !ui.aiPanelOpen });
            }}
          >
            <Sparkles /> Preguntar a la IA
          </Button>
        </SimpleTooltip>
      </div>
    </header>
  );
}
